import { Container, FillGradient, Graphics } from 'pixi.js';
import { BaseWidget } from 'ff/debrief/components/hud/widgets/BaseWidget';
import { CurrentPoint } from 'ff/debrief/models';
import { HudContext } from 'ff/debrief/components/hud/HudContext';
import { HudPositionTranslator } from 'ff/debrief/components/hud/utils';

export class HorizonWidget extends BaseWidget {
    static readonly requiredProperties = [
        'pitchDeg',
        'bankDeg',
        'altitudeFeet',
    ] as const;
    static readonly optionalProperties = [] as const;

    width: number;
    height: number;
    posX: number;
    posY: number;

    graphics: Graphics;
    container: Container;

    async populateElementContainer(
        elementContainer: Container,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): Promise<void> {
        this.container = elementContainer;
        // The horizon needs to be larger than its container to allow for rotation without gaps
        // The square we render needs to have height & width of the square root of 2 (1.414) to
        // cover all rotation angles without gaps. We use 1.5 to give a little extra margin.
        this.width = positionTranslator.x(1.5);
        this.height = positionTranslator.y(1.5);
        this.posX = positionTranslator.x(0);
        this.posY = positionTranslator.y(0);

        this.graphics = new Graphics();
        this.graphics.pivot.set(
            this.positionTranslator.x(0.75),
            this.positionTranslator.y(0.75)
        );
        this.graphics.position.set(
            this.positionTranslator.x(0.5),
            this.positionTranslator.y(0.5)
        );

        // Since we're making the horizon larger than the viewport, we need to mask it
        // so it doesn't overflow the HUD area.
        // We create a white rectangle the size of the viewport and use it as a mask.
        // Everything outside this rectangle will be hidden.
        // Note: The mask must be added to the container before setting it as the mask.
        // Otherwise, it won't render correctly.
        // Also, the mask must be in the same coordinate space as the container it masks.
        // So we create it using the position translator of the full HUD, not the widget.
        const hudPositionTranslator = hudContext.positionTranslator;
        const viewportMask = new Graphics()
            .rect(0, 0, hudPositionTranslator.x(1), hudPositionTranslator.y(1))
            .fill(0xffffff);
        elementContainer.addChild(viewportMask);
        elementContainer.mask = viewportMask;

        elementContainer.addChild(this.graphics);
    }

    protected updateWidget(point: CurrentPoint): void {
        const newFill = this.buildHorizonFill(
            this.calculatePitchOffset(
                point.pitchDeg,
                point.bankDeg,
                point.altitudeFeet
            )
        );
        this.graphics.clear();
        this.graphics
            .rect(this.posX, this.posY, this.width, this.height)
            .fill(newFill);
        // Update horizon graphics based on roll and pitch
        this.graphics.rotation = (-point.bankDeg * Math.PI) / 180; // Convert degrees to radians
    }

    private buildHorizonFill(horizonPercent: number): FillGradient {
        const scaled = horizonPercent * 0.5 + 0.5; // Scale to 0 to 1
        const clamped = Math.max(0.15, Math.min(0.85, scaled));

        // We adjust the offset of the color stops to move the horizon line up and down.
        // We start at 0.25 and end at 0.75 to ensure the gradient only applies to the middle half of the rectangle,
        // leaving the top and bottom quarters as solid colors only visible when the horizon is at extreme angles.
        return new FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 }, // Start at top
            end: { x: 0, y: 1 }, // End at bottom
            colorStops: [
                { offset: 0.15, color: '#2E4BC6' }, // Dark at start
                { offset: clamped, color: '#5B7FE6' }, // Light at end

                { offset: clamped, color: '#A67C52' }, // Light at start
                { offset: 0.85, color: '#704214' }, // Dark at end
            ],
            textureSpace: 'local',
        });
    }

    private calculatePitchOffset(
        pitch: number,
        bank: number,
        altitude: number
    ): number {
        /**
         *   Step-by-Step Breakdown
         *
         *   1. Normalize and Clamp Pitch (lines 102-107)
         *
         *   let _pitch = pitch;
         *   while (_pitch > 180) _pitch = pitch - 360;  // Normalize to -180 to +180
         *   if (_pitch > 60) _pitch = 60;                // Clamp to displayable range
         *   else if (_pitch < -60) _pitch = -60;
         *
         *   2. Calculate Geometric Horizon Angle (lines 109-115)
         *
         *   This is the key geometric calculation:
         *
         *            * Observer (aircraft)
         *           /|
         *          / |
         *         /  | altitude
         *        /   |
         *       /    |
         *      /θ    * ← Earth's surface at observer
         *     /  ___/|
         *    /___/   | Earth radius
         *   *--------* Earth's center
         *
         *   const distanceToHorizon = Math.sqrt((EARTH_RADIUS + altitude)² - EARTH_RADIUS²);
         *   const horizonAtAngle_radian = distanceToHorizon / EARTH_RADIUS;  // Small angle approximation
         *
         *   What this means: From altitude, the true horizon appears below the horizontal plane. At 10,000 ft, the horizon is ~1.9° below level flight. At 40,000 ft, it's ~3.7° below.
         *
         *   3. Apply Bank Correction (lines 116-117)
         *
         *   const addToPitch = horizonAtAngle_degrees * Math.cos((bank * Math.PI) / 180);
         *
         *   Why cosine? When banked, the "effective" horizon angle decreases:
         *   - At 0° bank: full horizon correction applies
         *   - At 45° bank: correction × 0.707
         *   - At 90° bank: correction × 0 (looking sideways, no altitude effect)
         *
         *   4. Return Tangent (line 119)
         *
         *   return Math.tan((_pitch * Math.PI) / 180);
         *
         *   The tangent converts the angle into a slope ratio used by buildHorizonFill to position the gradient.
         *
         *   Visual Example
         *
         *   Level Flight at 10,000 ft:
         *                                   Sky (blue)
         *   ─────────────────────────────────────────── ← Visual horizon
         *     │                                       ↑
         *     │ 1.9° depression angle                 │ This offset is what
         *     ↓                                        │ calculatePitchOffset
         *   ─────────────────────────────────────────── ← True horizon   returns
         *                                 Ground (brown)
         *
         *
         *   Pitched Up 10° at 10,000 ft:
         *                                   Sky (blue)
         *                             ↗
         *                        ↗
         *   ─────────────────────────────────────────── ← Visual horizon
         *                                               ↑
         *        Pitch: 10°                             │ Offset = tan(10° + 1.9°)
         *        Horizon adjustment: +1.9°              │        = tan(11.9°)
         *        Total: 11.9°                           │        ≈ 0.21
         *                                               ↓
         *   ─────────────────────────────────────────── ← True horizon
         *                                 Ground (brown)
         *
         *
         *   Banked 30° Right at 10,000 ft:
         *                                   Sky
         *                                 /
         *                               /      ← Horizon rotates
         *                             /          but altitude effect
         *                           /            is reduced by cos(30°)
         *   ─────────────────────/──────────────────────
         *                     /              Ground
         *
         *   Key Insight
         *
         *   The function accounts for the fact that at altitude, looking straight ahead doesn't show you the horizon — you're looking "over the curve" of the Earth. The higher you are, the more you need to pitch the
         *   aircraft down to see the true geometric horizon.
         */
        // used for calculating the angle to the horizon, from the altitude
        const EARTH_RADIUS = 2.09256e7; // in feet
        const EARTH_RADIUS_SQR = EARTH_RADIUS * EARTH_RADIUS;
        // split the altitude into its value and sign (e.g. 1 or -1) to accommodate for
        // negative altitudes (there's an airport in Israel @ -1100ft AGL)
        const altMagnitude = Math.abs(altitude);
        const altSign = altitude ? altitude / altMagnitude : 1;
        let _pitch = pitch;

        // normalize pitch to -180 to +180
        while (_pitch > 180) _pitch = pitch - 360;

        // clamp pitch to -60 to +60
        if (_pitch > 60) _pitch = 60;
        else if (_pitch < -60) _pitch = -60;

        // approx the viewing angle of the horizon based on the altitude
        const distanceToHorizon = Math.sqrt(
            (EARTH_RADIUS + altMagnitude) * (EARTH_RADIUS + altMagnitude) -
                EARTH_RADIUS_SQR
        );
        const horizonAtAngle_radian = distanceToHorizon / EARTH_RADIUS; // approximate arctan for small angles
        const horizonAtAngle_degrees = (horizonAtAngle_radian * 180) / Math.PI;
        const addToPitch =
            horizonAtAngle_degrees * Math.cos((bank * Math.PI) / 180);
        _pitch += addToPitch * altSign; // increase if positive altitude, decrease if negative altitude
        return Math.tan((_pitch * Math.PI) / 180);
    }
    cleanUp(): void {
        this.container?.destroy();
        this.container = null;

        this.graphics?.destroy();
        this.graphics = null;
    }
}
